import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from "rxjs";
import {ApiService} from "./api.service";
import {CommonUtils} from "../utils/common";
import {VetineoFile} from "../models/file/vetineo-file.model";
import {VetineoApiResponse} from "../models/vetineo-api-response.model";
import {catchError, map, switchMap} from "rxjs/operators";
import {HttpHeaders} from "@angular/common/http";
import {VImage} from "../models/vet-clinic.model";
import {AppStateService} from "./app-state.service";

/**
 * Serwis zarządzający plikami i multimediami.
 * Obsługuje przesyłanie (Upload), usuwanie oraz pobieranie plików zdjęciowych
 * dla klinik, artykułów oraz załączników do rezerwacji.
 */
@Injectable({
  providedIn: 'root'
})
export class FileService {
  /** Pamięć podręczna (Cache) dla lokalnych obrazków.
   */
  private _vImages = new BehaviorSubject<VImage[]>(null);

  constructor(
    private apiService: ApiService,
    public commonUtils: CommonUtils,
    private appStateService: AppStateService
  ) {
  }

  /**
   * Przesyła pojedynczy plik graficzny na główny serwer za pomocą formatu FormData.
   *
   * @param file Surowy obiekt pliku z API przeglądarki (HTML5 File API).
   * @returns Observable emitujący zinstancjonowany obiekt VImage zwrócony po udanym zapisie przez backend.
   */
  uploadFile(file: File): Observable<VImage> {
    const options = {headers: new HttpHeaders().set('Content-Type', 'multipart/form-data')};
    const formData: FormData = new FormData();
    formData.append('image', file, file.name);

    return this.apiService.put<VetineoApiResponse>('/dashboard/images/upload', formData, options).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok && vetineoApiResponse.response) {
        return this.commonUtils.bestTypedCopyEver<VImage>(new VImage(), vetineoApiResponse.response);
      }
    }));
  }

  /**
   * Przesyła plik bezpośrednio do sieci CDN, umożliwiając przypisanie go do konkretnego
   * folderu w chmurze z zachowaniem hierarchii klienta.
   *
   * @param vClientUUID Unikalny identyfikator nadrzędnego klienta.
   * @param vetClinicUUID Identyfikator konkretnej kliniki.
   * @param imageFolder Nazwa wirtualnego folderu docelowego na CDN.
   * @param file Surowy obiekt pliku do wysłania.
   * @returns Observable emitujący metadane pomyślnie przetworzonego obrazu (VImage).
   */
  uploadFileToCdn(vClientUUID: string, vetClinicUUID: string, imageFolder: string, file: File): Observable<VImage> {
    const options = {headers: new HttpHeaders().set('Content-Type', 'multipart/form-data')};
    const formData: FormData = new FormData();
    formData.append('image', file, file.name);
    formData.append('vClientUUID', vClientUUID);
    formData.append('vetClinicUUID', vetClinicUUID);
    formData.append('imageFolder', imageFolder);

    return this.apiService.put<VetineoApiResponse>('/dashboard/images/cdn-upload', formData, options).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok && vetineoApiResponse.response) {
        console.log(vetineoApiResponse.response);
        return this.commonUtils.bestTypedCopyEver<VImage>(new VImage(), vetineoApiResponse.response);
      }
    }));
  }

  /**
   * Usuwa obraz z serwerów CDN na podstawie jego unikalnego identyfikatora.
   *
   * @param vImageUUID Globalnie unikalny identyfikator pliku.
   * @returns Observable emitujący flagę logiczną potwierdzającą usunięcie.
   */
  deleteFromCdn(vImageUUID: string): Observable<boolean> {
    return this.apiService.delete<VetineoApiResponse>(`/dashboard/images/cdn-delete/${vImageUUID}`)
      .pipe(map(vetineoApiResponse => {
        console.log(vetineoApiResponse)
        if (vetineoApiResponse.ok && vetineoApiResponse.response) {
          return vetineoApiResponse.response
        }
      }));
  }

  /**
   * Pobiera listę ogólnych obrazów (np. galerii zdjęć) dla konkretnej placówki weterynaryjnej.
   * Wykorzystuje lokalny Cache (BehaviorSubject), chyba że wymuszono twarde odświeżenie.
   *
   * @param doRefresh Flaga nakazująca zignorowanie pamięci podręcznej i wysłanie fizycznego zapytania do API.
   * @param vetcodeId Identyfikator lecznicy.
   * @returns Observable emitujący tablicę zinstancjonowanych obiektów obrazów.
   */
  getVImages(doRefresh: boolean = false, vetcodeId): Observable<VImage[]> {
    if (this._vImages.getValue() && !doRefresh) {
      return this._vImages.asObservable();
    } else {
      return this.apiService.get<VetineoApiResponse>(`/dashboard/images/list/${vetcodeId}`).pipe(switchMap(vetineoApiResponse => {
        if (vetineoApiResponse.ok && vetineoApiResponse.response && vetineoApiResponse.response.images) {
          this.setVImages(this.commonUtils.bestTypedArrayCopyEver(new VImage(), [], vetineoApiResponse.response.images));
          return this._vImages.asObservable();
        } else {
          this.setVImages([]);
          return this._vImages.asObservable();
        }
      }), catchError(error => {
        this.appStateService.updateIsBusySubject(false);
        return [];
      }));
    }
  }

  /**
   * Pobiera galerię obrazów przypisaną na poziomie klienta.
   */
  getClientImages(doRefresh: boolean = false, vClientUUID): Observable<VImage[]> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/images/list/client/${vClientUUID}`).pipe(map(vetineoApiResponse => {
        if (vetineoApiResponse.ok && vetineoApiResponse.response && vetineoApiResponse.response.images) {
           return this.commonUtils.bestTypedArrayCopyEver(new VImage(), [], vetineoApiResponse.response.images);
        } else {
          return [];
      }}));
  }

  /**
   * Pobiera zasoby multimedialne przypisane do Partnera.
   */
  getPartnerVImages(partnerVClientUUID: string, doRefresh: boolean = false): Observable<VImage[]> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/partner/${partnerVClientUUID}/images/list`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok && vetineoApiResponse.response && vetineoApiResponse.response.images) {
        return this.commonUtils.bestTypedArrayCopyEver(new VImage(), [], vetineoApiResponse.response.images);
      } else {
        return [];
      }
    }));
  }

  /**
   * Aktualizuje zawartość lokalnej pamięci podręcznej (Cache) obrazów.
   */
  setVImages(vImages: VImage[]) {
    this._vImages.next(vImages);
  }

  /**
   * Pobiera pojedynczy, szczegółowy plik z załączników konkretnej rezerwacji pacjenta.
   *
   * @param fileName Dokładna nazwa pliku.
   * @param reservationUuid Identyfikator powiązanej rezerwacji medycznej.
   * @returns Observable emitujący model VFile (często z bezpiecznym URL-em lub base64).
   */
  getVetReservationFile(fileName: string, reservationUuid: string): Observable<VetineoFile> {
    return this.apiService.get<VetineoApiResponse>('/dashboard/vet-reservation/v-file/' + reservationUuid + '/' + fileName).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok && vetineoApiResponse.response) {
        return this.commonUtils.bestTypedCopyEver<VetineoFile>(new VetineoFile(), vetineoApiResponse.response);
      }
    }));
  }


  /**
   * Pobiera zoptymalizowane, lekkie miniatury (Thumbnails) zdjęć dodanych do rezerwacji.
   *
   * @param vetReservationUuid Unikalny identyfikator rezerwacji.
   * @returns Observable emitujący tablicę zoptymalizowanych miniatur.
   */
  getThumbnails(vetReservationUuid: string): Observable<VetineoFile[]> {
    return this.apiService.get<VetineoApiResponse>('/dashboard/vet-reservation/thumbnails/' + vetReservationUuid).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok && vetineoApiResponse.response) {
        console.log(vetineoApiResponse.response);
        return this.commonUtils.bestTypedArrayCopyEver(new VetineoFile(), [], vetineoApiResponse.response);
      }
    }));
  }

  /**
   * Pobiera pełną listę plików medycznych dołączonych do wskazanej rezerwacji.
   *
   * @param vetReservationUUID Identyfikator rezerwacji.
   * @returns Observable emitujący tablicę instancji VetineoFile.
   */
  getVetReservationFiles(vetReservationUUID: string): Observable<VetineoFile[]> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/vet-reservation/v-files/${vetReservationUUID}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok && vetineoApiResponse.response) {
        return this.commonUtils.bestTypedArrayCopyEver<VetineoFile, VetineoFile[]>(new VetineoFile(), [], vetineoApiResponse.response);
      }
    }, error => {
      console.log(error);
      return [];
    }));
  }

  /**
   * Pobiera zasoby zdjęciowe dedykowane dla artykułów dla wskazanego klienta.
   */
  getImagesForArticles(vClientUUID: string) : Observable<VImage[]> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/${vClientUUID}/images/articles/list`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok && vetineoApiResponse.response && vetineoApiResponse.response.images) {
        return this.commonUtils.bestTypedArrayCopyEver(new VImage(), [], vetineoApiResponse.response.images);
      } else {
        return [];
      }
    }));
  }
}
